import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Send, Trash2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { call, getJson, mediaUrl, postForm } from "@/lib/api";
import { postJson, useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { AuthorLink, Avatar, JoinPrompt, PendingNote, PhotoPicker, useIsMine, useTimeAgo } from "./shared";
import { PostActions } from "./PostActions";

const Composer = ({ onPosted }) => {
  const { t } = useLang();
  const { member } = useAuth();
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  if (!member) return <JoinPrompt next="/comunidad?tab=pared" />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const p = await postForm("/community/wall", { body, file });
      setBody("");
      setFile(null);
      setPending(p.status === "pending");
      if (p.status === "published") onPosted(p);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[var(--card)] p-4" data-testid="wall-composer">
      <div className="flex gap-3">
        <Avatar author={member} />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} required maxLength={1000} rows={3} placeholder={t("com.wallPh")} className="support-input resize-none" data-testid="wall-composer-input" />
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 pl-12">
        <PhotoPicker file={file} setFile={setFile} testid="wall-photo-input" />
        <button type="submit" disabled={busy || !body.trim()} className="btn-primary !py-2 disabled:opacity-50" data-testid="wall-submit-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {t("com.post")}
        </button>
      </div>
      <PendingNote show={pending} />
    </form>
  );
};

export const Post = ({ p, ws }) => {
  const { t } = useLang();
  const ago = useTimeAgo();
  const mine = useIsMine()(p);
  return (
    <article className="mb-4 break-inside-avoid rounded-2xl border border-white/10 bg-[var(--card)] p-4" data-testid={`wall-post-${p.id}`}>
      <header className="flex items-center gap-3">
        <AuthorLink author={p.author}><Avatar author={p.author} size={32} /></AuthorLink>
        <div className="min-w-0 flex-1">
          <AuthorLink author={p.author} className="block truncate text-sm font-semibold hover:text-[var(--green)]" testid={`wall-author-${p.id}`}>{p.author?.name}</AuthorLink>
          <Link to={`/comunidad/pared/${p.id}`} className="mono text-[11px] text-[var(--muted)] hover:text-[var(--ink)]" data-testid={`wall-permalink-${p.id}`}>{ago(p.created_at)}</Link>
        </div>
        {p.from_followed && <span className="mono inline-flex items-center gap-1 rounded-full border border-[var(--green)]/40 px-2 py-0.5 text-[11px] uppercase tracking-[0.12em] text-[var(--green)]" data-testid={`wall-followed-badge-${p.id}`}><UserCheck size={11} /> {t("follow.on")}</span>}
        {mine && <button type="button" onClick={() => ws.remove(p.id)} className="icon-btn" aria-label={t("com.delete")} data-testid={`wall-delete-${p.id}`}><Trash2 size={14} /></button>}
      </header>
      <p className="mt-3 whitespace-pre-line text-sm leading-relaxed">{p.body}</p>
      {p.image_url && <img src={mediaUrl(p.image_url)} alt="" className="mt-3 w-full rounded-xl" loading="lazy" />}
      <PostActions post={p} liked={ws.likes.includes(p.id)} saved={ws.saved.includes(p.id)} onLike={ws.like} onSave={ws.save} canReport={!mine} needLogin={ws.needLogin} />
    </article>
  );
};

/** Likes + favorites state shared by the wall, a single post page and the profile. */
export const useWallState = (setPosts) => {
  const { t } = useLang();
  const { member } = useAuth();
  const navigate = useNavigate();
  const [st, setSt] = useState({ likes: [], saved: [] });
  useEffect(() => {
    if (member) getJson("/community/me/state").then((d) => setSt({ likes: d?.likes ?? [], saved: d?.saved ?? [] }));
    else setSt({ likes: [], saved: [] });
  }, [member]);
  const needLogin = member ? null : () => toast(t("com.joinToPost"), { action: { label: t("auth.login"), onClick: () => navigate("/entrar?next=/comunidad?tab=pared") } });
  const toggleIn = (key, id, on) => setSt((s) => ({ ...s, [key]: on ? [...s[key], id] : s[key].filter((x) => x !== id) }));
  const like = async (id) => {
    if (needLogin) return needLogin();
    try {
      const r = await postJson(`/community/wall/${id}/like`, null);
      toggleIn("likes", id, r.liked);
      setPosts((s) => s.map((p) => (p.id === id ? { ...p, like_count: r.like_count } : p)));
    } catch (e) {
      toast.error(e.message);
    }
  };
  const save = async (id) => {
    if (needLogin) return needLogin();
    const r = await postJson(`/community/wall/${id}/save`, null);
    toggleIn("saved", id, r.saved);
    toast.success(t(r.saved ? "save.added" : "save.removed"));
  };
  const remove = async (id) => {
    if (!window.confirm(t("com.confirmDelete"))) return;
    await call(`/community/post/${id}`, { method: "DELETE" });
    setPosts((s) => s.filter((p) => p.id !== id));
  };
  return { ...st, like, save, remove, needLogin };
};

export const Wall = () => {
  const { t } = useLang();
  const { member } = useAuth();
  const [posts, setPosts] = useState(null);
  const [onlyFollowed, setOnlyFollowed] = useState(false);
  const ws = useWallState(setPosts);
  useEffect(() => {
    if (member === null) return;
    getJson("/community/wall").then((d) => setPosts(Array.isArray(d) ? d : []));
  }, [member]);
  const shown = onlyFollowed ? (posts ?? []).filter((p) => p.from_followed) : posts;
  return (
    <div className="mx-auto max-w-5xl" data-testid="community-wall">
      <div className="mx-auto max-w-2xl"><Composer onPosted={(p) => setPosts((s) => [p, ...(s ?? [])])} /></div>
      <div className="mt-8 flex justify-center gap-2" data-testid="wall-filter">
        {[[false, "wall.all"], [true, "com.tab.siguiendo"]].map(([v, key]) => (
          <button key={key} type="button" onClick={() => (v && !member ? ws.needLogin() : setOnlyFollowed(v))} className={`col-chip ${onlyFollowed === v ? "col-chip-on" : ""}`} data-testid={`wall-filter-${v ? "following" : "all"}`}>{t(key)}</button>
        ))}
      </div>
      {shown === null && <Loader2 className="mx-auto mt-12 animate-spin" />}
      {shown?.length === 0 && (onlyFollowed
        ? <p className="mx-auto mt-12 max-w-md text-center text-sm text-[var(--muted)]" data-testid="wall-following-empty">{t("follow.noneBody")}</p>
        : <p className="mt-12 text-center text-[var(--muted)]" data-testid="wall-empty">{t("com.wallEmpty")}</p>)}
      {shown?.length > 0 && (
        <div className="mt-8 gap-4 sm:columns-2 lg:columns-3">
          {shown.map((p) => <Post key={p.id} p={p} ws={ws} />)}
        </div>
      )}
    </div>
  );
};
